import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NavigateArrow } from '../../../assets/icons/device-management/navigate-back.svg';
import { RoleAddInterFace, RoleInterFace } from '../../../interface/role-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllPermissions } from '../../../redux/slices/RolesManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import { stringValidation } from '../../../shared-functions/Validation';
import roleApi from '../../../utils/api/roles';

interface AddRoleInterface {
  closeModal: () => void;
  roleDetail?: RoleInterFace;
  completedTask: (message: string, type: string) => void;
}
/**
 * Role Add page function component
 */
function AddRole({ closeModal, roleDetail, completedTask }: AddRoleInterface) {
  const {t}=useTranslation('userRole')
  const dispatch = useAppDispatch();
  const { permissionsList, loading } = useAppSelector((state) => state.roles);

  const [disabled, setDisabled] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [roleDetails, setRoleDetails] = useState<RoleAddInterFace>({
    name: roleDetail?.name || '',
    responsibility: roleDetail?.responsibility || '',
    permissions: roleDetail?.permissions || [],
  });

  /**
   * Handle the input change function
   * @param name
   * @param value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setRoleDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the submit function
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    if (
      stringValidation(1, 100, roleDetails.name) &&
      stringValidation(1, 100, roleDetails.responsibility) &&
      roleDetails.permissions.length > 0
    ) {
      setDisabled(true);
      const request = {
        name: roleDetails.name,
        responsibility: roleDetails.responsibility,
        permissions: roleDetails.permissions,
      };
      if (roleDetail?.id) {
        roleApi
          .editRoles(roleDetail?.id, request)
          .then(() => {
            setDisabled(false);
            completedTask(t('roleUpdatedSuccessfully'), 'role');
          })
          .catch((error) => {
            setDisabled(false);
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: error?.response?.data?.code,
              }),
            );
          });
      } else {
        roleApi
          .createRoles(request)
          .then(() => {
            setDisabled(false);
            completedTask(t('roleAddedSuccessfully'), 'role');
          })
          .catch((error) => {
            setDisabled(false);
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: error?.response?.data?.code,
              }),
            );
          });
      }
    }
  };

  useEffect(() => {
    if (!loading && permissionsList.length === 0) {
      dispatch(fetchAllPermissions());
    }
  }, []);

  return (
    <div className="add-role-side-panel">
      <div className="panel-header">
        <div className="header-title-container">
          <span role="presentation" onClick={closeModal} className="navigate-arrow-span">
            <NavigateArrow />
          </span>
          <h3>{roleDetail?.id ? t('editRole') : t('addRole')}</h3>
        </div>
      </div>

      <div
        style={{
          height: window.innerHeight >= 700 ? 398 : 710,
        }}
      >
        <div className="add-role-group">
          <div className="profile-card">
            <div className="edit-fields-container">
              <div className="role-header-section">
                <h4>{t('addUserRole')}</h4>
                {/* <p>{t('rolePermissionDescription')}</p> */}
              </div>

              <div className="info-row-input">
                <CustomInput
                  name="name"
                  inputLabel={t('userRole')}
                  type="text"
                  placeholder={t('enterRole')}
                  value={roleDetails.name}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && !stringValidation(1, 100, roleDetails.name)}
                  errorMessage={t('roleNameRequired')}
                />
                <CustomInput
                  name="responsibility"
                  inputLabel={t('responsibility')}
                  type="text"
                  placeholder={t('describeHere')}
                  value={roleDetails.responsibility}
                  onChangeValue={handleInputChange}
                  error={showFormErrors && !stringValidation(1, 100, roleDetails.responsibility)}
                  valueRequired
                  errorMessage={t('responsibilityRequired')}
                />
              </div>

              <div className="info-row-input">
                <CheckBoxButtons
                  name="permissions"
                  list={permissionsList}
                  inputLabel={t('permissions')}
                  value={roleDetails.permissions}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && roleDetails.permissions.length === 0}
                  errorMessage={t('permissionsRequired')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="footer-actions">
          <MyButton
            label={t('cancel')}
            buttonType="outline"
            onClickFunc={closeModal}
          />
          <MyButton
            label={roleDetail?.id ? t('update') : t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmit}
            disabled={disabled}
            loading={disabled}
          />
        </div>
      </div>
    </div>
  );
}

AddRole.defaultProps = {
  roleDetail: {},
};

export default AddRole;
